import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import type { ContentRouteFolderEntry } from '@/lib/api/content-route-index.server';

export type ContentFolderHeaderOptimisticState = {
    title: string;
    description: string;
    showDescriptionSkeleton: boolean;
    showSubcategoriesSkeleton: boolean;
    showTitleSkeleton: boolean;
    setPendingNavigationTarget: (target: { id: string; name: string }) => void;
    isChanging: boolean;
};

export function useContentFolderHeaderOptimisticState(
    folder: ContentRouteFolderEntry
): ContentFolderHeaderOptimisticState {
    const [pendingTarget, setPendingTarget] = useState<{ id: string; name: string } | null>(null);
    const navigation = useNavigation();
    const location = useLocation();

    const isPathnameChanging = Boolean(
        navigation.state !== 'idle' && navigation.location && navigation.location.pathname !== location.pathname
    );

    const isChanging = pendingTarget !== null || isPathnameChanging;

    const pendingDirectChild = useMemo(
        () => folder.children.find((child) => child.id === pendingTarget?.id),
        [folder.children, pendingTarget]
    );

    const isDirectChildNavigation = Boolean(pendingDirectChild);

    const title =
        pendingTarget && !pendingDirectChild ? pendingTarget.name : (pendingDirectChild?.title ?? folder.title);

    const description = pendingDirectChild ? (pendingDirectChild.description ?? '') : (folder.description ?? '');

    const showDescriptionSkeleton = isChanging && (!isDirectChildNavigation || !description.trim());
    const showSubcategoriesSkeleton = isChanging;
    const showTitleSkeleton = isChanging && !pendingTarget;

    useEffect(() => {
        if (navigation.state === 'idle') {
            setPendingTarget(null);
        }
    }, [navigation.state]);

    return {
        title,
        description,
        showDescriptionSkeleton,
        showSubcategoriesSkeleton,
        showTitleSkeleton,
        setPendingNavigationTarget: setPendingTarget,
        isChanging,
    };
}
